import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { ApiKey } from '../../_shared/domain/api-key.model';
import { initialState } from './api-keys.state';

/**
 * API keys index slice. Reducers are pure state transitions only; the fetch,
 * the create POST and the revoke DELETE all live in the saga, which dispatches
 * these `load*` / `create*` / `revoke*` transitions as it progresses.
 */
const slice = createSlice({
  name: 'apiKeys',
  initialState,
  reducers: {
    // ── Load lifecycle ──
    loadStarted: (state) => {
      state.loading = true;
      state.error = null;
    },
    loadSucceeded: (state, action: PayloadAction<ApiKey[]>) => {
      state.rows = action.payload;
      state.loading = false;
    },
    loadFailed: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    // ── Create lifecycle ──
    createStarted: (state) => {
      state.createStatus = 'saving';
      state.error = null;
    },
    /** The raw secret is stashed so the dialog can show it once. */
    createSucceeded: (state, action: PayloadAction<string>) => {
      state.createStatus = 'succeeded';
      state.createdKey = action.payload;
    },
    createFailed: (state, action: PayloadAction<string>) => {
      state.createStatus = 'failed';
      state.error = action.payload;
    },
    /** Dismiss the create dialog; clears the one-time secret and the status. */
    createReset: (state) => {
      state.createStatus = 'idle';
      state.createdKey = null;
    },
    // ── Revoke lifecycle ──
    revokeStarted: (state) => {
      state.revokeStatus = 'saving';
      state.error = null;
    },
    revokeSucceeded: (state) => {
      state.revokeStatus = 'succeeded';
    },
    revokeFailed: (state, action: PayloadAction<string>) => {
      state.revokeStatus = 'failed';
      state.error = action.payload;
    },
  },
});

export const apiKeysActions = slice.actions;
export const apiKeysReducer = slice.reducer;
